import { useEffect, useRef, useTransition } from "react";

import type { RefetchFnDynamic } from "react-relay";
import type { OperationType } from "relay-runtime";

export type PollingKey = Readonly<{
  " $data"?: unknown;
  " $fragmentSpreads": unknown;
}>;

export type PollingQuery = OperationType;

interface UsePollingRefetchableOptions {
  /** Interval in milliseconds for polling */
  interval: number;
  /** Whether polling is enabled */
  enabled?: boolean;
  /**
   * Variables to include in each poll refetch. Updated via a ref so changing
   * variables does not restart the interval — the next tick always uses the
   * latest value. Omitted variables fall back to the parent query's values.
   */
  variables?: Record<string, unknown>;
}

/**
 * Custom hook for polling data at regular intervals using a refetch function
 * @param refetch - Function to call for refetching data
 * @param options - Polling configuration options
 */
export function usePollingRefetchable<
  TQuery extends PollingQuery,
  TKey extends PollingKey | null | undefined,
>(
  refetch: RefetchFnDynamic<TQuery, TKey>,
  options: UsePollingRefetchableOptions,
): void {
  const { interval, enabled = true, variables = {} } = options;
  const [, startTransition] = useTransition();

  // Track latest variables in a ref so the interval closure always uses the
  // current value without needing to restart the interval on every change.
  // Written from an effect (not during render) to satisfy the React Compiler;
  // interval ticks always fire after effects have run, so they see the latest
  // committed value either way.
  const variablesRef = useRef(variables);
  useEffect(() => {
    variablesRef.current = variables;
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const intervalId = setInterval(() => {
      startTransition(() => {
        refetch(variablesRef.current, { fetchPolicy: "store-and-network" });
      });
    }, interval);

    return () => clearInterval(intervalId);
  }, [refetch, interval, enabled]);
}
